import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useVaults } from '../hooks/useVaults';
import { useAuth } from '../hooks/useAuth';

interface Department {
  id: string;
  name: string;
  code: string;
  lead: string;
  vaultIds: string[];
  quotaDocs: number;
  quotaTokens: number;
  membersCount: number;
}

interface Member {
  id: string;
  name: string;
  email: string;
  departmentId: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer' | 'guest';
  docsUsed: number;
  docsLimit: number;
  tokensUsed: number;
  tokensLimit: number;
  status: 'active' | 'suspended';
}

interface AuditEvent {
  id: string;
  user: string;
  department: string;
  action: string;
  detail: string;
  timestamp: string;
  ip: string;
  status: string;
}

export function WorkspacePage() {
  const { tenant } = useAuth();
  const { vaults } = useVaults();
  const [activeTab, setActiveTab] = useState<'departments' | 'members' | 'policies' | 'audit'>('departments');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Security Policies State
  const [policies, setPolicies] = useState({
    require2fa: true,
    enforcePrivacyGuard: true,
    watermarkDownloads: true,
    dualApprovalDelete: true,
    autoOcrAllFiles: true,
  });

  // Modal States
  const [showDepModal, setShowDepModal] = useState(false);
  const [editingDep, setEditingDep] = useState<Department | null>(null);
  const [depForm, setDepForm] = useState({ name: '', code: '', lead: '', quotaDocs: 2000, quotaTokens: 500000 });

  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: '', email: '', departmentId: '', role: 'editor' as Member['role'], docsLimit: 1000, tokensLimit: 200000 });

  // Initial Data Load
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [depRes, auditRes] = await Promise.all([
        api.getWorkspaceDepartments().catch(() => ({ departments: [] })),
        api.getWorkspaceAudit().catch(() => ({ events: [] }))
      ]);
      setDepartments(depRes.departments || []);
      setAuditLogs(auditRes.events || []);

      // Mock members based on departments
      const sampleMembers: Member[] = [
        { id: 'm_1', name: tenant?.name || 'Nguyễn Văn Eric', email: 'eric@wowerix.com', departmentId: 'dep_bod', role: 'owner', docsUsed: 840, docsLimit: 5000, tokensUsed: 125400, tokensLimit: 1000000, status: 'active' },
        { id: 'm_2', name: 'Trần Thị Thu', email: 'thu.tran@wowerix.com', departmentId: 'dep_legal', role: 'admin', docsUsed: 312, docsLimit: 2000, tokensUsed: 42100, tokensLimit: 500000, status: 'active' },
        { id: 'm_3', name: 'Lê Hoàng Nam', email: 'nam.le@wowerix.com', departmentId: 'dep_fin', role: 'admin', docsUsed: 450, docsLimit: 3000, tokensUsed: 98000, tokensLimit: 500000, status: 'active' },
        { id: 'm_4', name: 'Phạm Quốc Hùng', email: 'hung.pham@wowerix.com', departmentId: 'dep_tech', role: 'editor', docsUsed: 1200, docsLimit: 4000, tokensUsed: 450000, tokensLimit: 1000000, status: 'active' },
        { id: 'm_5', name: 'Đỗ Mai Trang', email: 'trang.do@wowerix.com', departmentId: 'dep_hr', role: 'editor', docsUsed: 180, docsLimit: 1500, tokensUsed: 15400, tokensLimit: 300000, status: 'active' },
        { id: 'm_6', name: 'Vũ Đức Minh', email: 'minh.vu@wowerix.com', departmentId: 'dep_sales', role: 'viewer', docsUsed: 95, docsLimit: 1000, tokensUsed: 22000, tokensLimit: 300000, status: 'active' },
        { id: 'm_7', name: 'Đối Tác Thẩm Định Ngoài', email: 'partner.auditor@external.vn', departmentId: 'dep_legal', role: 'guest', docsUsed: 12, docsLimit: 100, tokensUsed: 4500, tokensLimit: 50000, status: 'active' }
      ];
      setMembers(sampleMembers);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDepartment = async () => {
    if (!depForm.name.trim() || !depForm.code.trim()) {
      alert('Vui lòng nhập tên và mã phòng ban!');
      return;
    }
    setSaving(true);
    let updated: Department[];
    if (editingDep) {
      updated = departments.map(d => d.id === editingDep.id ? { ...d, ...depForm } : d);
    } else {
      const newDep: Department = {
        id: `dep_${Date.now()}`,
        name: depForm.name.trim(),
        code: depForm.code.trim().toUpperCase(),
        lead: depForm.lead.trim() || 'Chưa phân công',
        vaultIds: [],
        quotaDocs: Number(depForm.quotaDocs) || 2000,
        quotaTokens: Number(depForm.quotaTokens) || 500000,
        membersCount: 1
      };
      updated = [...departments, newDep];
    }
    try {
      await api.saveWorkspaceDepartments(updated);
      setDepartments(updated);
      setShowDepModal(false);
      setEditingDep(null);
      setDepForm({ name: '', code: '', lead: '', quotaDocs: 2000, quotaTokens: 500000 });
    } catch (e: any) {
      alert('Lưu phòng ban thất bại: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async (id: string, name: string) => {
    if (!confirm(`Bạn có chắc muốn xóa phòng ban "${name}"?`)) return;
    const updated = departments.filter(d => d.id !== id);
    try {
      await api.saveWorkspaceDepartments(updated);
      setDepartments(updated);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const toggleVaultAssignment = async (depId: string, vaultId: string) => {
    const updated = departments.map(d => {
      if (d.id === depId) {
        const has = d.vaultIds.includes(vaultId);
        const vaultIds = has ? d.vaultIds.filter(v => v !== vaultId) : [...d.vaultIds, vaultId];
        return { ...d, vaultIds };
      }
      return d;
    });
    setDepartments(updated);
    await api.saveWorkspaceDepartments(updated).catch(() => null);
  };

  const handleAddMember = () => {
    if (!memberForm.name.trim() || !memberForm.email.trim()) {
      alert('Vui lòng nhập họ tên và email thành viên!');
      return;
    }
    const newMember: Member = {
      id: `m_${Date.now()}`,
      name: memberForm.name.trim(),
      email: memberForm.email.trim(),
      departmentId: memberForm.departmentId || (departments[0]?.id ?? 'dep_bod'),
      role: memberForm.role,
      docsUsed: 0,
      docsLimit: memberForm.docsLimit,
      tokensUsed: 0,
      tokensLimit: memberForm.tokensLimit,
      status: 'active'
    };
    setMembers([newMember, ...members]);
    setShowMemberModal(false);
    setMemberForm({ name: '', email: '', departmentId: '', role: 'editor', docsLimit: 1000, tokensLimit: 200000 });
    alert(`🎉 Đã gửi thư mời gia nhập Workspace tới ${newMember.email}`);
  };

  const handleRoleChange = (memberId: string, newRole: Member['role']) => {
    setMembers(members.map(m => m.id === memberId ? { ...m, role: newRole } : m));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang nạp dữ liệu Enterprise Workspace...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px', overflowY: 'auto', paddingRight: '4px' }}>

      {/* Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 className="page-title">🏢 Enterprise Workspace B2B</h1>
            <span className="badge badge--purple" style={{ fontSize: '11px', padding: '3px 10px', background: 'rgba(124, 58, 237, 0.25)', border: '1px solid rgba(124, 58, 237, 0.5)', color: '#c084fc' }}>
              Multi-Department Architecture
            </span>
          </div>
          <p className="page-subtitle">
            Quản trị không gian làm việc doanh nghiệp đa phòng ban, phân quyền đa tầng, gán Kho báu dữ liệu và nhật ký truy vết
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ background: 'rgba(255,255,255,0.04)', padding: '6px 14px', borderRadius: '10px', border: '1px solid var(--border)', display: 'flex', gap: '16px', fontSize: '12px' }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Phòng ban:</span> <strong>{departments.length}</strong></div>
            <div><span style={{ color: 'var(--text-muted)' }}>Thành viên:</span> <strong>{members.length}</strong></div>
            <div><span style={{ color: 'var(--text-muted)' }}>Kho báu gán:</span> <strong>{vaults.length}</strong></div>
          </div>
          <button
            className="btn btn--primary"
            onClick={() => {
              setEditingDep(null);
              setDepForm({ name: '', code: '', lead: '', quotaDocs: 2000, quotaTokens: 500000 });
              setShowDepModal(true);
            }}
          >
            ➕ Thêm Phòng Ban
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', flexShrink: 0 }}>
        <button
          className={`btn ${activeTab === 'departments' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setActiveTab('departments')}
        >
          🏢 Phòng Ban & Kho Báu ({departments.length})
        </button>
        <button
          className={`btn ${activeTab === 'members' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setActiveTab('members')}
        >
          👥 Thành Viên & Quyền Hạn ({members.length})
        </button>
        <button
          className={`btn ${activeTab === 'policies' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setActiveTab('policies')}
        >
          🛡️ Chính Sách Bảo Mật Doanh Nghiệp
        </button>
        <button
          className={`btn ${activeTab === 'audit' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setActiveTab('audit')}
        >
          📜 Nhật Ký Hoạt Động (Audit Log)
        </button>
      </div>

      {/* TAB 1: DEPARTMENTS & VAULTS MAPPING */}
      {activeTab === 'departments' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
          {departments.map(dep => {
            const depMembers = members.filter(m => m.departmentId === dep.id);
            return (
              <div
                key={dep.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>🏢</span>
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#f1f5f9' }}>{dep.name}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                      <span className="badge badge--blue" style={{ fontSize: '10px' }}>{dep.code}</span>
                      <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>Lead: <strong>{dep.lead}</strong></span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      className="btn btn--ghost btn--sm"
                      title="Sửa phòng ban"
                      onClick={() => {
                        setEditingDep(dep);
                        setDepForm({ name: dep.name, code: dep.code, lead: dep.lead, quotaDocs: dep.quotaDocs, quotaTokens: dep.quotaTokens });
                        setShowDepModal(true);
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn--danger btn--sm"
                      title="Xóa phòng ban"
                      onClick={() => handleDeleteDepartment(dep.id, dep.name)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Quotas */}
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Hạn mức tài liệu:</span>
                    <div style={{ fontWeight: 600, color: 'var(--accent-cyan)' }}>{dep.quotaDocs.toLocaleString()} files</div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>AI Tokens / tháng:</span>
                    <div style={{ fontWeight: 600, color: 'var(--accent-purple)' }}>{(dep.quotaTokens / 1000).toLocaleString()}k tokens</div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Nhân sự:</span>
                    <div style={{ fontWeight: 600, color: '#34d399' }}>{depMembers.length} người</div>
                  </div>
                </div>

                {/* Assigned Vaults */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>📁 Kho báu dữ liệu gán cho phòng ban:</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>({dep.vaultIds.length} kho báu)</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {vaults.map(v => {
                      const isAssigned = dep.vaultIds.includes(v.id);
                      return (
                        <button
                          key={v.id}
                          onClick={() => toggleVaultAssignment(dep.id, v.id)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            background: isAssigned ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isAssigned ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.1)'}`,
                            color: isAssigned ? '#38bdf8' : 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={isAssigned ? 'Bấm để gỡ Kho báu khỏi phòng ban' : 'Bấm để gán Kho báu cho phòng ban'}
                        >
                          <span>{isAssigned ? '✅' : '➕'}</span>
                          <span>{v.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TAB 2: MEMBERS & MULTI-TIER ROLES */}
      {activeTab === 'members' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Phân quyền thành viên theo 5 cấp độ: <strong>Owner</strong> (Chủ quyền) &gt; <strong>Admin</strong> (Quản trị) &gt; <strong>Editor</strong> (Biên tập) &gt; <strong>Viewer</strong> (Chỉ đọc) &gt; <strong>Guest</strong> (Khách đối tác).
            </div>
            <button className="btn btn--secondary btn--sm" onClick={() => setShowMemberModal(true)}>
              ➕ Thêm Thành Viên
            </button>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Thành viên</th>
                  <th>Phòng ban</th>
                  <th>Vai trò phân quyền</th>
                  <th>Hạn mức tài liệu</th>
                  <th>AI Query Tokens</th>
                  <th>Trạng thái</th>
                  <th style={{ width: '100px' }}>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const dep = departments.find(d => d.id === m.departmentId);
                  return (
                    <tr key={m.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{m.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{m.email}</div>
                      </td>
                      <td>
                        <span className="badge badge--gray" style={{ fontSize: '11px' }}>
                          {dep?.name || 'Chung'}
                        </span>
                      </td>
                      <td>
                        <select
                          className="input"
                          value={m.role}
                          onChange={e => handleRoleChange(m.id, e.target.value as Member['role'])}
                          style={{ padding: '4px 8px', fontSize: '12px', width: 'auto' }}
                        >
                          <option value="owner">👑 Owner</option>
                          <option value="admin">🛡️ Admin</option>
                          <option value="editor">✍️ Editor</option>
                          <option value="viewer">👁️ Viewer</option>
                          <option value="guest">🤝 Guest</option>
                        </select>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          <strong>{m.docsUsed}</strong> / {m.docsLimit} files
                        </div>
                        <div style={{ width: '100px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min((m.docsUsed / m.docsLimit) * 100, 100)}%`, height: '100%', background: 'var(--accent-cyan)' }} />
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          <strong>{(m.tokensUsed / 1000).toFixed(1)}k</strong> / {(m.tokensLimit / 1000).toFixed(0)}k
                        </div>
                        <div style={{ width: '100px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min((m.tokensUsed / m.tokensLimit) * 100, 100)}%`, height: '100%', background: 'var(--accent-purple)' }} />
                        </div>
                      </td>
                      <td>
                        <span className="badge badge--green" style={{ fontSize: '10px' }}>
                          ● {m.status === 'active' ? 'Đang hoạt động' : 'Tạm khóa'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn--danger btn--sm"
                          onClick={() => {
                            if (confirm(`Xóa thành viên ${m.name} khỏi Workspace?`)) {
                              setMembers(members.filter(x => x.id !== m.id));
                            }
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: SECURITY POLICIES */}
      {activeTab === 'policies' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '16px' }}>
          {[
            {
              key: 'require2fa',
              title: '🔒 Bắt buộc xác thực 2FA (Two-Factor Authentication)',
              desc: 'Tất cả nhân sự thuộc các phòng ban đều phải xác thực mã TOTP trước khi truy cập vào các Kho báu dữ liệu quan trọng.',
              enabled: policies.require2fa
            },
            {
              key: 'enforcePrivacyGuard',
              title: '🛡️ Privacy Guard bắt buộc trước khi chat AI',
              desc: 'Hệ thống tự động quét và che giấu (Mask) các thông tin nhạy cảm (Số CCCD, thẻ tín dụng, API Key, số điện thoại) trước khi gửi prompt lên mô hình ngôn ngữ.',
              enabled: policies.enforcePrivacyGuard
            },
            {
              key: 'watermarkDownloads',
              title: '💧 Đóng dấu chìm Watermark khi xuất PDF/Word',
              desc: 'Tự động gắn họ tên, email, dấu thời gian và ID người tải xuống trên mọi file xuất bản nhằm chống rò rỉ dữ liệu mật.',
              enabled: policies.watermarkDownloads
            },
            {
              key: 'dualApprovalDelete',
              title: '🔐 Cơ chế kiểm duyệt kép khi xóa tài liệu',
              desc: 'Tài liệu trong Kho báu doanh nghiệp bị xóa sẽ chuyển vào thùng rác tạm 30 ngày và cần tối thiểu 1 Admin phê duyệt để xóa vĩnh viễn.',
              enabled: policies.dualApprovalDelete
            },
            {
              key: 'autoOcrAllFiles',
              title: '⚡ Tự động OCR & Tạo Vector Index cho file mới',
              desc: 'Mọi tài liệu PDF quét hoặc ảnh chụp tải lên sẽ được chạy qua pipeline nhận diện chữ và nạp vào Cloudflare Vectorize ngay lập tức.',
              enabled: policies.autoOcrAllFiles
            }
          ].map(p => (
            <div
              key={p.key}
              style={{
                background: 'var(--bg-card)',
                border: `1px solid ${p.enabled ? 'rgba(56, 189, 248, 0.4)' : 'var(--border)'}`,
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '12px'
              }}
            >
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#f1f5f9' }}>{p.title}</h3>
                <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{p.desc}</p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button
                  className={`btn ${p.enabled ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                  onClick={() => setPolicies(prev => ({ ...prev, [p.key]: !(prev as any)[p.key] }))}
                >
                  {p.enabled ? '🟢 Đang áp dụng' : '⚪ Đã tắt'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 4: AUDIT LOG */}
      {activeTab === 'audit' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Nhật ký lưu trữ toàn bộ các hoạt động nhạy cảm trong không gian làm việc doanh nghiệp theo tiêu chuẩn tuân thủ SOC2 / ISO 27001.
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Thời gian</th>
                  <th>Người thực hiện</th>
                  <th>Phòng ban</th>
                  <th>Hành động</th>
                  <th>Chi tiết</th>
                  <th>Địa chỉ IP</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(ev => (
                  <tr key={ev.id}>
                    <td style={{ fontSize: '11.5px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(ev.timestamp).toLocaleString('vi-VN')}
                    </td>
                    <td style={{ fontWeight: 600 }}>{ev.user}</td>
                    <td><span className="badge badge--gray">{ev.department}</span></td>
                    <td>
                      <span className="badge badge--purple" style={{ fontSize: '10px' }}>
                        {ev.action}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', maxWidth: '300px' }}>{ev.detail}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>{ev.ip}</td>
                    <td>
                      <span className="badge badge--green" style={{ fontSize: '10px' }}>
                        ● {ev.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: DEPARTMENT */}
      {showDepModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '480px' }}>
            <div className="modal__header">
              <h2 className="modal__title">{editingDep ? 'Chỉnh Sửa Phòng Ban' : 'Thêm Phòng Ban Mới'}</h2>
              <button className="modal__close" onClick={() => setShowDepModal(false)}>✕</button>
            </div>
            <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Tên Phòng Ban</label>
                <input
                  className="input"
                  value={depForm.name}
                  onChange={e => setDepForm({ ...depForm, name: e.target.value })}
                  placeholder="VD: Phòng Pháp Chế & Tuân Thủ"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Mã (Code)</label>
                  <input
                    className="input"
                    value={depForm.code}
                    onChange={e => setDepForm({ ...depForm, code: e.target.value })}
                    placeholder="LEGAL"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Trưởng Bộ Phận (Lead)</label>
                  <input
                    className="input"
                    value={depForm.lead}
                    onChange={e => setDepForm({ ...depForm, lead: e.target.value })}
                    placeholder="VD: Trần Thị Thu"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Hạn mức Documents</label>
                  <input
                    type="number"
                    className="input"
                    value={depForm.quotaDocs}
                    onChange={e => setDepForm({ ...depForm, quotaDocs: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Hạn mức AI Tokens/tháng</label>
                  <input
                    type="number"
                    className="input"
                    value={depForm.quotaTokens}
                    onChange={e => setDepForm({ ...depForm, quotaTokens: Number(e.target.value) })}
                  />
                </div>
              </div>
            </div>
            <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn--secondary" onClick={() => setShowDepModal(false)}>Hủy</button>
              <button className="btn btn--primary" onClick={handleSaveDepartment} disabled={saving}>
                {saving ? 'Đang lưu...' : 'Lưu Phòng Ban'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: MEMBER */}
      {showMemberModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '480px' }}>
            <div className="modal__header">
              <h2 className="modal__title">Mời Thành Viên Mới Vào Doanh Nghiệp</h2>
              <button className="modal__close" onClick={() => setShowMemberModal(false)}>✕</button>
            </div>
            <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Họ Và Tên</label>
                <input
                  className="input"
                  value={memberForm.name}
                  onChange={e => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="VD: Nguyễn Văn A"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Email Doanh Nghiệp</label>
                <input
                  type="email"
                  className="input"
                  value={memberForm.email}
                  onChange={e => setMemberForm({ ...memberForm, email: e.target.value })}
                  placeholder="nva@company.com"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Phòng Ban</label>
                  <select
                    className="input"
                    value={memberForm.departmentId}
                    onChange={e => setMemberForm({ ...memberForm, departmentId: e.target.value })}
                  >
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Vai Trò Phân Quyền</label>
                  <select
                    className="input"
                    value={memberForm.role}
                    onChange={e => setMemberForm({ ...memberForm, role: e.target.value as any })}
                  >
                    <option value="admin">🛡️ Admin</option>
                    <option value="editor">✍️ Editor</option>
                    <option value="viewer">👁️ Viewer</option>
                    <option value="guest">🤝 Guest</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn--secondary" onClick={() => setShowMemberModal(false)}>Hủy</button>
              <button className="btn btn--primary" onClick={handleAddMember}>
                Gửi Thư Mời
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
